import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import Layout from '../../components/Layout';
import './Settings.css';
import './DangerZone.css';

function ConfirmModal({ title, description, confirmText, onConfirm, onCancel, requireType }) {
  const [input, setInput] = useState('');
  const [password, setPassword] = useState('');
  const ready = requireType ? input === requireType && password.length >= 6 : password.length >= 6;

  return (
    <div className="kiosk-overlay" onClick={onCancel}>
      <div className="kiosk-modal" onClick={e => e.stopPropagation()}>
        <div className="kiosk-modal-header">
          <h2 style={{color:'#ef4444'}}>{title}</h2>
          <button className="kiosk-modal-close" onClick={onCancel}>✕</button>
        </div>
        <p className="dz-confirm-desc">{description}</p>
        {requireType && (
          <div className="spage-field">
            <label>Type <strong style={{color:'#ef4444'}}>{requireType}</strong> to confirm</label>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={requireType}
              autoFocus
            />
          </div>
        )}
        <div className="spage-field">
          <label>Enter your password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>
        <div className="kiosk-modal-actions">
          <button className="spage-btn-upgrade" onClick={onCancel}>Cancel</button>
          <button
            className="dz-confirm-btn"
            disabled={!ready}
            onClick={() => onConfirm(password)}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DangerZone() {
  const { org, logout } = useAuth();
  const [modal, setModal]     = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError]     = useState('');

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };
  const showError   = (msg) => { setError(msg);   setTimeout(() => setError(''), 4000); };

  const handleDeleteJobs = async (password) => {
    try {
      await client.delete('/jobs/all', { data: { password } });
      setModal(null);
      showSuccess('All jobs deleted successfully.');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete jobs');
      setModal(null);
    }
  };

  const handleResetInventory = async (password) => {
    try {
      await client.post('/inventory/reset', { password });
      setModal(null);
      showSuccess('Inventory reset to zero.');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to reset inventory');
      setModal(null);
    }
  };

  const handleCancelSubscription = async (password) => {
    try {
      await client.post('/org/cancel', { password });
      setModal(null);
      showSuccess('Subscription cancelled. Your account remains active until the end of the billing period.');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to cancel subscription');
      setModal(null);
    }
  };

  const handleDeleteOrg = async (password) => {
    try {
      await client.delete('/org', { data: { password } });
      setModal(null);
      logout();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete organization');
      setModal(null);
    }
  };

  const ACTIONS = [
    {
      id: 'delete-jobs',
      title: 'Delete All Jobs',
      badge: '2-STEP VERIFICATION',
      description: 'Permanently deletes every job in your organization. Inventory and users are unaffected. This cannot be undone.',
      btnLabel: 'Delete All Jobs',
      requireType: 'DELETE',
      onConfirm: handleDeleteJobs,
    },
    {
      id: 'reset-inventory',
      title: 'Reset Inventory',
      description: 'Resets all inventory counts to zero across every warehouse and location. Job data is unaffected. This cannot be undone.',
      btnLabel: 'Reset Inventory',
      onConfirm: handleResetInventory,
    },
    {
      id: 'cancel-sub',
      title: 'Cancel Subscription',
      description: 'Cancels your subscription at the end of the current billing period. Your account remains active until then.',
      btnLabel: 'Cancel Subscription',
      onConfirm: handleCancelSubscription,
    },
    {
      id: 'delete-org',
      title: 'Delete Organization',
      badge: '2-STEP VERIFICATION',
      description: `Permanently deletes your organization, all jobs, all inventory, all users, and all data. There is no recovery. This cannot be undone.`,
      btnLabel: 'Delete Organization',
      requireType: org?.slug,
      onConfirm: handleDeleteOrg,
    },
  ];

  return (
    <Layout>
      <div className="spage-header">
        <h1>Danger Zone</h1>
        <p className="spage-sub">These actions are permanent and cannot be reversed. Proceed with caution.</p>
      </div>

      {success && <div className="spage-success">{success}</div>}
      {error   && <div className="spage-error">{error}</div>}

      <div className="dz-list">
        {ACTIONS.map(action => (
          <div key={action.id} className="dz-row">
            <div className="dz-row-left">
              <div className="dz-row-title">
                {action.title}
                {action.badge && <span className="dz-badge">{action.badge}</span>}
              </div>
              <div className="dz-row-desc">{action.description}</div>
            </div>
            <button className="dz-btn" onClick={() => setModal(action)}>
              {action.btnLabel}
            </button>
          </div>
        ))}
      </div>

      {modal && (
        <ConfirmModal
          title={modal.title}
          description={modal.description}
          confirmText={modal.btnLabel}
          requireType={modal.requireType}
          onConfirm={modal.onConfirm}
          onCancel={() => setModal(null)}
        />
      )}
    </Layout>
  );
}